"use client";

import { config } from "@repo/config";
import { OrganizationInvitationAlert } from "@saas/organizations/components/OrganizationInvitationAlert";
import { useRouter } from "@shared/hooks/router";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "@shared/components/error-boundary";
import {
	type OAuthProvider,
	oAuthProviders,
} from "../constants/oauth-providers";
import { useSession } from "../hooks/use-session";
import { SocialSigninButton } from "./SocialSigninButton";

export interface LoginFormProps {
	redirectTo?: string;
}

// 内部组件包含 useSearchParams 调用
function LoginFormInner({ redirectTo: redirectProp }: LoginFormProps) {
	const t = useTranslations();
	const router = useRouter();
	const searchParams = useSearchParams();
	const { user, loaded: sessionLoaded } = useSession();

	const invitationId = searchParams.get("invitationId");
	const email = searchParams.get("email");
	const redirectToParam = searchParams.get("redirectTo");

	// 表单打开时，将重定向路径保存到 localStorage，以便社交登录使用
	useEffect(() => {
		// 重定向路径优先顺序：邀请ID > redirectProp > URL参数 > 默认路径
		const path = invitationId
			? `/app/organization-invitation/${invitationId}`
			: (redirectProp ??
				redirectToParam ??
				config.auth.redirectAfterSignIn);

		if (typeof window !== "undefined" && path) {
			try {
				localStorage.setItem("modalRedirectPath", path);
				console.log("Saved modalRedirectPath to localStorage:", path);
			} catch (e) {
				console.error("Error saving to localStorage:", e);
			}
		}

		// 清理函数
		return () => {
			if (typeof window !== "undefined") {
				try {
					localStorage.removeItem("modalRedirectPath");
				} catch (e) {
					console.error("Error removing from localStorage:", e);
				}
			}
		};
	}, [invitationId, redirectProp, redirectToParam]);

	// 重定向路径优先顺序：邀请ID > redirectProp > URL参数 > 默认路径
	const redirectPath = invitationId
		? `/app/organization-invitation/${invitationId}`
		: (redirectProp ?? redirectToParam ?? config.auth.redirectAfterSignIn);

	console.log(
		"LoginForm redirectPath:",
		redirectPath,
		"redirectProp:",
		redirectProp,
	);

	useEffect(() => {
		if (sessionLoaded && user) {
			console.log(
				"User already logged in, redirecting to:",
				redirectPath,
			);
			// 使用 setTimeout 确保在 DOM 更新后再进行跳转
			setTimeout(() => {
				router.replace(redirectPath);
			}, 0);
		}
	}, [user, sessionLoaded, redirectPath, router]);

	return (
		<div>
			<h1 className="font-extrabold text-2xl md:text-3xl text-primary">
				{t("auth.login.title")}
			</h1>
			<p className="mt-1 mb-6 text-foreground/60">
				{t("auth.login.subtitle")}
			</p>

			{invitationId && <OrganizationInvitationAlert className="mb-6" />}

			<div>
				{config.auth.enableSignup && config.auth.enableSocialLogin && (
					<ErrorBoundary>
						<div>
							{Object.keys(oAuthProviders).map((providerId) => (
								<SocialSigninButton
									key={providerId}
									provider={providerId as OAuthProvider}
									className="w-full mb-2"
									redirectTo={redirectPath}
								/>
							))}
						</div>
					</ErrorBoundary>
				)}
			</div>
		</div>
	);
}

// 导出组件包含 Suspense 边界
export function LoginForm(props: LoginFormProps) {
	return (
		<Suspense fallback={<div>Loading...</div>}>
			<LoginFormInner {...props} />
		</Suspense>
	);
}
